import { usePostHog } from 'posthog-js/react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useTier } from '../../context/TierContext';

const LockIcon = ({ size = 12, color = '#93a8c4' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M17 10V8A5 5 0 0 0 7 8v2M12 14.5v2M8.8 21h6.4c1.68 0 2.52 0 3.16-.33a3 3 0 0 0 1.31-1.31C20 18.72 20 17.88 20 16.2v-.4c0-1.68 0-2.52-.33-3.16a3 3 0 0 0-1.31-1.31C17.72 11 16.88 11 15.2 11H8.8c-1.68 0-2.52 0-3.16.33a3 3 0 0 0-1.31 1.31C4 13.28 4 14.12 4 15.8v.4c0 1.68 0 2.52.33 3.16a3 3 0 0 0 1.31 1.31C6.28 21 7.12 21 8.8 21Z"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

/**
 * ProLock - Unified gating wrapper for Pro-gated surfaces.
 *
 * Modes:
 * - hidden: Render upgrade CTA placeholder instead of children
 * - blur: Render children with CSS blur (full or selective via blurTargets)
 * - overlay: Render children blurred with a frosted glass unlock CTA positioned on top
 * - badge: Render children + small Pro badge overlay
 * - silent: Render nothing (null)
 * - interactive: Render children; onTrigger fires an upgrade overlay
 */
const ProLock = ({
  mode,
  feature,
  blurTargets = false,
  blurAmount = 8,
  onTrigger,
  // D-020: source prop for upgrade modal attribution. Defaults to free_header_cta if not provided.
  source = 'free_header_cta',
  children,
}) => {
  const { hasProAccess, triggerUpgradeModal, trialUnlockEligible } = useTier();
  const posthog = usePostHog();

  if (hasProAccess) {
    return <>{children}</>;
  }

  // Step 8 (two-phase trial): a treatment-arm, in-flight cardless trial user is
  // unlocking a FREE trial, not buying Pro. Drop the paid "Pro"/"Upgrade" framing
  // and show a plain "Unlock" on every gated CTA. Control / ineligible users keep
  // the existing copy. The card-entry surface routing is handled downstream by the
  // upgrade-intent bus in app/_layout (trialUnlockEligible -> TrialUnlockModal).
  const ctaLabel = (defaultLabel) => (trialUnlockEligible ? 'Unlock' : defaultLabel);

  const handleUpgradeTap = () => {
    if (posthog) {
      posthog.capture('prolock_triggered', { feature, mode });
    }
    if (onTrigger) {
      onTrigger();
    } else {
      // D-020: use triggerUpgradeModal instead of router.push('/pricing')
      triggerUpgradeModal({ source });
    }
  };

  switch (mode) {
    case 'hidden':
      return (
        <Pressable onPress={handleUpgradeTap} style={styles.hiddenContainer}>
          <View style={styles.ctaBadge}>
            <Text style={styles.ctaBadgeText}>Pro</Text>
          </View>
          <Text style={styles.hiddenText}>{ctaLabel('Upgrade to unlock')}</Text>
        </Pressable>
      );

    case 'blur':
      if (blurTargets) {
        // Label-visible blur: CSS rule blurs only [data-blur-target] children.
        // Labels stay crisp, values blurred. CTA at the bottom.
        return (
          <View>
            <View dataSet={{ prolockGated: 'true' }}>
              {children}
            </View>
            <Pressable onPress={handleUpgradeTap} style={styles.blurCta}>
              <View style={styles.ctaBadge}>
                <Text style={styles.ctaBadgeText}>Pro</Text>
              </View>
              <Text style={styles.blurCtaText}>{ctaLabel('Unlock all factors')}</Text>
            </Pressable>
          </View>
        );
      }
      // Full blur: blur all children
      return (
        <Pressable onPress={handleUpgradeTap}>
          <View style={webStyles.fullBlur} pointerEvents="none">
            {children}
          </View>
        </Pressable>
      );

    case 'overlay':
      // Blurred content with frosted glass unlock CTA on top
      return (
        <View style={styles.overlayContainer}>
          <View style={webStyles.overlayBlur} pointerEvents="none">
            {children}
          </View>
          <Pressable onPress={handleUpgradeTap} style={styles.overlayCtaContainer}>
            <View style={styles.overlayCta}>
              <View style={styles.ctaBadge}>
                <Text style={styles.ctaBadgeText}>Pro</Text>
              </View>
              <Text style={styles.overlayCtaText}>{ctaLabel('Unlock with Pro')}</Text>
            </View>
          </Pressable>
        </View>
      );

    case 'badge':
      return (
        <Pressable onPress={handleUpgradeTap} style={styles.badgeWrapper}>
          <View pointerEvents="none">
            {children}
          </View>
          <View style={styles.proBadge}>
            <Text style={styles.proBadgeText}>Pro</Text>
          </View>
        </Pressable>
      );

    case 'silent':
      return null;

    case 'interactive':
      return (
        <Pressable onPress={handleUpgradeTap}>
          <View pointerEvents="none">
            {children}
          </View>
        </Pressable>
      );

    default:
      return <>{children}</>;
  }
};

// Web-only styles using CSS properties not in StyleSheet.create
const webStyles = {
  fullBlur: {
    filter: 'blur(8px)',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  overlayBlur: {
    filter: 'blur(3px)',
    userSelect: 'none',
    opacity: 0.75,
  },
};

const styles = StyleSheet.create({
  // Hidden mode
  hiddenContainer: {
    padding: 16,
    backgroundColor: REDESIGN_COLORS?.surfaceElevated || '#1a1a2e',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: REDESIGN_COLORS?.border || '#2a2a4a',
    alignItems: 'center',
    gap: 8,
  },
  hiddenText: {
    fontSize: 13,
    fontFamily: 'Satoshi-Medium',
    color: REDESIGN_COLORS?.textSecondary || '#9ca3af',
  },
  // Blur mode CTA
  blurCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
  },
  blurCtaText: {
    fontSize: 13,
    fontFamily: 'Satoshi-Bold',
    color: '#3b82f6',
  },
  // Shared CTA badge (hidden + overlay + blur)
  ctaBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: '#3b82f6',
    borderRadius: 4,
  },
  ctaBadgeText: {
    fontSize: 11,
    fontFamily: 'Satoshi-Bold',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Overlay mode
  overlayContainer: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 8,
  },
  overlayCtaContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  overlayCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: 'rgba(4, 4, 9, 0.75)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  overlayCtaText: {
    fontSize: 13,
    fontFamily: 'Satoshi-Bold',
    color: '#ffffff',
  },
  // Badge mode
  badgeWrapper: {
    position: 'relative',
  },
  proBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: '#3b82f6',
    borderRadius: 4,
    zIndex: 1,
  },
  proBadgeText: {
    fontSize: 9,
    fontFamily: 'Satoshi-Bold',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});

/**
 * ProInlineUnlock - Small inline unlock button that replaces a value.
 * Shows children when Pro, shows a small "Pro" tap target when Free.
 */
export const ProInlineUnlock = ({ feature, source = 'free_header_cta', children }) => {
  const { hasProAccess, triggerUpgradeModal } = useTier();
  const posthog = usePostHog();

  if (hasProAccess) return <>{children}</>;

  return (
    <Pressable
      onPress={() => {
        if (posthog) posthog.capture('prolock_triggered', { feature, mode: 'inline' });
        // D-020: use triggerUpgradeModal instead of router.push('/pricing')
        triggerUpgradeModal({ source });
      }}
      style={inlineStyles.button}
    >
      <LockIcon size={11} color="#7a8fa6" />
      <Text style={inlineStyles.text}>Unlock</Text>
    </Pressable>
  );
};

const inlineStyles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(122, 143, 166, 0.1)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(122, 143, 166, 0.2)',
  },
  text: {
    fontSize: 11,
    fontFamily: 'Satoshi-Medium',
    color: '#7a8fa6',
    letterSpacing: 0.3,
  },
});

export default ProLock;
